"use client";

import { createClient } from "../supabase/client";
import { mapComment, mapDoc, mapIssue, mapNote } from "../storeMappers";
import type { Doc, Issue, Note } from "../types";

export type WorkspaceEntityTargetInput = {
  workspaceId: string;
  kind: "issue" | "doc" | "note";
  id: string;
  signal?: AbortSignal;
};

/** RLS intentionally makes missing, deleted and inaccessible rows indistinguishable. */
export type WorkspaceEntityTargetResult =
  | { status: "found"; kind: "issue"; entity: Issue }
  | { status: "found"; kind: "doc"; entity: Doc }
  | { status: "found"; kind: "note"; entity: Note }
  | { status: "unavailable" }
  | { status: "error" };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISSUE_COLUMNS = "id,identifier,workspace_id,team_id,title,description,status,is_blocked,priority,assignee_id,owner_id,executor_agent_id,requester_id,report_channel,report_context,report_url,project_id,cycle_id,parent_id,author_id,created_at,updated_at,completed_at,due_date,estimate,deleted_at,is_public";
const DOC_COLUMNS = "id,identifier,workspace_id,project_id,team_id,parent_id,position,title,content,icon,author_id,tags,pinned,created_at,updated_at,deleted_at,is_public";
const NOTE_COLUMNS = "id,workspace_id,project_id,team_id,body,author_id,tags,pinned,created_at,updated_at,deleted_at";
const COMMENT_COLUMNS = "id,issue_id,parent_type,parent_id,author_id,body,reply_to_id,created_at,updated_at";

/**
 * Fetch one live route target outside the potentially capped bootstrap list.
 * The browser session's RLS applies, and the URL workspace is always a query
 * predicate. Never look up an ID globally to infer or switch its workspace.
 * Transport/query failures and cancellation remain retryable errors, not 404s.
 */
export async function loadWorkspaceEntityTarget({
  workspaceId: rawWorkspaceId, kind, id: rawId, signal,
}: WorkspaceEntityTargetInput): Promise<WorkspaceEntityTargetResult> {
  if (signal?.aborted) return { status: "error" };
  if (!UUID.test(rawWorkspaceId) || !UUID.test(rawId) || !["issue", "doc", "note"].includes(kind)) {
    return { status: "unavailable" };
  }
  const workspaceId = rawWorkspaceId.toLowerCase();
  const id = rawId.toLowerCase();
  try {
    const client = createClient();
    if (kind === "issue") {
      let query = client.from("issues").select(ISSUE_COLUMNS)
        .eq("id", id).eq("workspace_id", workspaceId).is("deleted_at", null);
      if (signal) query = query.abortSignal(signal);
      const { data: row, error } = await query.maybeSingle();
      if (signal?.aborted || error) return { status: "error" };
      if (!row || row.id !== id || row.workspace_id !== workspaceId || row.deleted_at !== null) {
        return { status: "unavailable" };
      }
      let labelsQuery = client.from("issue_labels").select("issue_id,label_id").eq("issue_id", id);
      let commentsQuery = client.from("comments").select(COMMENT_COLUMNS)
        .eq("parent_type", "issue").eq("parent_id", id).order("created_at", { ascending: true });
      if (signal) {
        labelsQuery = labelsQuery.abortSignal(signal);
        commentsQuery = commentsQuery.abortSignal(signal);
      }
      const [labels, comments] = await Promise.all([labelsQuery, commentsQuery]);
      if (signal?.aborted || labels.error || comments.error) return { status: "error" };
      return {
        status: "found", kind,
        entity: mapIssue(row,
          (labels.data ?? []).filter((label) => label.issue_id === id).map((label) => label.label_id),
          (comments.data ?? []).filter((comment) => comment.parent_type === kind && comment.parent_id === id)),
      };
    }

    if (kind === "note") {
      let query = client.from("notes").select(NOTE_COLUMNS)
        .eq("id", id).eq("workspace_id", workspaceId).is("deleted_at", null);
      if (signal) query = query.abortSignal(signal);
      const { data: row, error } = await query.maybeSingle();
      if (signal?.aborted || error) return { status: "error" };
      if (!row || row.id !== id || row.workspace_id !== workspaceId || row.deleted_at !== null) return { status: "unavailable" };
      let commentsQuery = client.from("comments").select(COMMENT_COLUMNS)
        .eq("parent_type", "note").eq("parent_id", id).order("created_at", { ascending: true });
      if (signal) commentsQuery = commentsQuery.abortSignal(signal);
      const comments = await commentsQuery;
      if (signal?.aborted || comments.error) return { status: "error" };
      return { status: "found", kind, entity: { ...mapNote(row), comments: (comments.data ?? [])
        .filter((comment) => comment.parent_type === kind && comment.parent_id === id).map(mapComment) } };
    }
    let query = client.from("docs").select(DOC_COLUMNS)
      .eq("id", id).eq("workspace_id", workspaceId).is("deleted_at", null);
    if (signal) query = query.abortSignal(signal);
    const { data: row, error } = await query.maybeSingle();
    if (signal?.aborted || error) return { status: "error" };
    if (!row || row.id !== id || row.workspace_id !== workspaceId || row.deleted_at !== null) {
      return { status: "unavailable" };
    }
    let commentsQuery = client.from("comments").select(COMMENT_COLUMNS)
      .eq("parent_type", kind).eq("parent_id", id).order("created_at", { ascending: true });
    if (signal) commentsQuery = commentsQuery.abortSignal(signal);
    const comments = await commentsQuery;
    if (signal?.aborted || comments.error) return { status: "error" };
    return {
      status: "found", kind,
      entity: { ...mapDoc(row), comments: (comments.data ?? [])
        .filter((comment) => comment.parent_type === kind && comment.parent_id === id).map(mapComment) },
    };
  } catch {
    return { status: "error" };
  }
}
